import { useEffect } from "react";
import { useTenant } from "@/contexts/TenantContext";

interface HslTriple {
  h: number;
  s: number;
  l: number;
}

function parseHex(hex: string): HslTriple | null {
  const clean = hex.replace(/^#/, "");
  if (!/^[0-9A-Fa-f]{6}$/.test(clean)) {
    console.warn(`Invalid hex color: ${hex}`);
    return null;
  }

  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslVar({ h, s, l }: HslTriple): string {
  return `${h} ${s}% ${l}%`;
}

// Relative luminance of an HSL color (for choosing legible on-brand ink)
function relativeLuminanceFromHsl({ h, s, l }: HslTriple): number {
  // Convert HSL → RGB, then compute WCAG luminance
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;
  let r1 = 0, g1 = 0, b1 = 0;
  if (h < 60) [r1, g1, b1] = [c, x, 0];
  else if (h < 120) [r1, g1, b1] = [x, c, 0];
  else if (h < 180) [r1, g1, b1] = [0, c, x];
  else if (h < 240) [r1, g1, b1] = [0, x, c];
  else if (h < 300) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];

  const channel = (v: number) => {
    const n = v + m;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r1) + 0.7152 * channel(g1) + 0.0722 * channel(b1);
}

/**
 * Applies tenant branding as CSS custom properties.
 *
 * Sets:
 *   --brand         — tenant primary (used for buttons, accents, focus rings)
 *   --brand-ink     — text color that reads on --brand (auto black/white)
 *   --brand-soft    — light tinted surface derived from brand hue
 *   --primary/etc.  — legacy shadcn tokens kept in sync for un-redesigned pages
 */
export function useTenantTheme(): void {
  const { tenant } = useTenant();

  useEffect(() => {
    if (!tenant) return;

    const root = document.documentElement;
    const brandHex =
      tenant.branding?.primaryColor || tenant.publicPrimaryColor || null;
    const accentHex =
      tenant.branding?.accentColor || tenant.publicSecondaryColor || null;

    if (brandHex) {
      const brand = parseHex(brandHex);
      if (brand) {
        root.style.setProperty("--brand", hslVar(brand));

        // Pick legible on-brand ink: white if brand is dark, near-black if light
        const lum = relativeLuminanceFromHsl(brand);
        const brandInk = lum > 0.5 ? "22 14% 9%" : "0 0% 100%";
        root.style.setProperty("--brand-ink", brandInk);

        // Soft brand surface: same hue, desaturated, very light
        const softSat = Math.min(brand.s, 40);
        const softLight = 96;
        root.style.setProperty(
          "--brand-soft",
          `${brand.h} ${softSat}% ${softLight}%`,
        );

        // Keep legacy --primary in sync so un-redesigned pages still look branded
        root.style.setProperty("--primary", hslVar(brand));
        root.style.setProperty("--primary-foreground", brandInk);
        root.style.setProperty("--ring", hslVar(brand));
      }
    }

    if (accentHex) {
      const accent = parseHex(accentHex);
      if (accent) {
        root.style.setProperty("--secondary", hslVar(accent));
      }
    }

    return () => {
      root.style.removeProperty("--brand");
      root.style.removeProperty("--brand-ink");
      root.style.removeProperty("--brand-soft");
      root.style.removeProperty("--primary");
      root.style.removeProperty("--primary-foreground");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--secondary");
    };
  }, [tenant]);
}

export default useTenantTheme;
